import { v4 as uuidv4 } from "uuid";

// Extension validation constants
export const EXTENSION_CONSTRAINTS = {
  MAX_WAIT_SECONDS: 60,
  MIN_WAIT_SECONDS: 0,
};

// Validation utilities
export const validatePressStep = (step) => {
  return step.digit?.trim() && /^\d+$/.test(step.digit);
};

export const validateWaitStep = (step) => {
  const { waitSeconds } = step;
  return (
    waitSeconds !== "" &&
    waitSeconds >= EXTENSION_CONSTRAINTS.MIN_WAIT_SECONDS &&
    waitSeconds <= EXTENSION_CONSTRAINTS.MAX_WAIT_SECONDS
  );
};

export const validateStep = (step) => {
  const validators = {
    press: validatePressStep,
    wait: validateWaitStep,
  };
  return validators[step.type]?.(step) ?? false;
};

export const validateSteps = (steps) => steps.every(validateStep);

// Utility function to generate extension preview text
export const generateExtensionPreviewText = (extensionSteps) => {
  if (!extensionSteps || extensionSteps.length === 0) {
    return "No extension steps configured";
  }

  const validSteps = extensionSteps.filter((step) => {
    if (step.type === "press") {
      return validatePressStep(step);
    } else if (step.type === "wait") {
      return step.waitSeconds > 0; // Skip zero-second waits
    }
    return false;
  });

  if (validSteps.length === 0) {
    return "No extension steps configured";
  }

  const stepTexts = validSteps
    .map((step) => {
      if (step.type === "press") {
        return `press ${step.digit}`;
      } else if (step.type === "wait") {
        return step.waitSeconds === 1
          ? "wait 1 second"
          : `wait ${step.waitSeconds} seconds`;
      }
      return "";
    })
    .filter(Boolean);

  const result = stepTexts.join(", then ");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

// Helper function to parse extension into step format
export const parseExtensionToSteps = (extension) => {
  if (!extension) return [];

  // Handle array format (from frontend)
  if (Array.isArray(extension)) {
    return extension
      .map((item) => {
        if (item.press) {
          return {
            type: "press",
            digit: item.press,
            id: uuidv4(),
          };
        } else if (item.wait) {
          return {
            type: "wait",
            waitSeconds: item.wait,
            id: uuidv4(),
          };
        }
        return null;
      })
      .filter(Boolean);
  }

  // Handle JSON string format
  if (typeof extension === "string") {
    try {
      const extensionArray = JSON.parse(extension);
      if (Array.isArray(extensionArray)) {
        return extensionArray
          .map((item) => {
            if (item.press) {
              return {
                type: "press",
                digit: item.press,
                id: uuidv4(),
              };
            } else if (item.wait) {
              return {
                type: "wait",
                waitSeconds: item.wait,
                id: uuidv4(),
              };
            }
            return null;
          })
          .filter(Boolean);
      }
    } catch (e) {
      console.error("Failed to parse extension string:", e);
    }
  }

  return [];
};
